import React, { useEffect, useState } from 'react';
import { FormContext, useForm } from 'react-hook-form';
import { StatusManager } from 'common/components/StatusManager';
import { HTTPStatusCodes } from 'constants/httpStatusCodes';
import { ENDPOINTS } from 'features/daily-plan/constants';
import { useShowAlert } from 'hooks';
import isEmpty from 'lodash/isEmpty';
import API from 'services/API';
import strings from 'strings';
import { When } from 'UI/components/atoms/When';
import FPButtonBuilder from 'UI/components/molecules/FPButtonBuilder';
import EmptyPlaceholder from 'UI/components/templates/EmptyPlaceholder';
import { buildCloseAndSaveButtonDefinition } from 'UI/components/templates/FPDrawer';
import FPDrawer from 'UI/components/templates/FPDrawer/FPDrawer';
import { UIStatus } from 'UI/constants/status';
import { FORM_FIELDS_BY_TASK_MAP } from 'UI/pages/DailyPlan/DailyPlan.constants';
import { SvgNoResults } from 'UI/res';
import { preloadFromBackend } from 'UI/utils/forms';

import { TaskResults } from '../../forms';
import { TASK_RESULTS_FORM_FIELDS_MAP } from '../../forms/TaskResults';
import { ChangeLogDrawer } from '../ChangeLogDrawer';
import { CreationTaskDrawer } from '../CreationTaskDrawer';

import { CommentTimeline, TaskDetails } from './components';
import { useTaskDetails } from './TaskDetailsDrawer.hooks';
import { CommentsContainer, DetailsContainer, StyledDivider } from './TaskDetailsDrawer.styled';
import { formatRegularTaskData } from './TaskDetailsDrawer.utils';

const { taskDetails: COPIES } = strings.dailyPlan.drawer;

export const TaskDetailsDrawer = ({ onClose, taskId, onTaskUpdated }) => {
  const {
    status,
    error,
    task,
    formattedDate,
    parsedEntity,
    refreshData: refreshTaskData
  } = useTaskDetails(taskId);
  const { showAlert } = useShowAlert();
  const form = useForm();
  const { handleSubmit, reset } = form;

  const [uiState, setUIState] = useState({
    [UIStatus.Loading]: false,
    isSaving: false,
    isFetchingInterview: false,
    isFormTaskDrawerOpen: false,
    isChangeLogDrawerOpen: false
  });
  const [taskFormValues, setTaskFormValues] = useState(null);

  useEffect(() => {
    if (!task) return;

    const defaultValues = preloadFromBackend(task, TASK_RESULTS_FORM_FIELDS_MAP);
    if (isEmpty(defaultValues)) return;

    reset(defaultValues);
  }, [reset, task]);

  const onSubmit = async formData => {
    try {
      setUIState(prev => ({
        ...prev,
        isSaving: true
      }));

      const dataToSend = formatRegularTaskData({ formData });

      const response = await API.patch(ENDPOINTS.task.replace(':id', taskId), dataToSend);

      if (response.status === HTTPStatusCodes.Ok) {
        showAlert({
          title: strings.dailyPlan.title,
          body: COPIES.form.alert.success,
          autoHideDuration: 5000
        });
        setUIState(prev => ({
          ...prev,
          isSaving: false
        }));
        onTaskUpdated && onTaskUpdated();
        onClose && onClose();
      }
    } catch (e) {
      showAlert({
        error: e,
        title: strings.dailyPlan.title,
        autoHideDuration: 5000
      });
      setUIState(prev => ({
        ...prev,
        isSaving: false
      }));
    }
  };

  const handleEditClickButton = () => {
    const taskTypeId = task.taskType?.id;
    const formFieldsConfig = FORM_FIELDS_BY_TASK_MAP[taskTypeId];
    const formattedData = preloadFromBackend(task, formFieldsConfig);
    setTaskFormValues(formattedData);
    setUIState(prev => ({ ...prev, isFormTaskDrawerOpen: true }));
  };

  const handleCloseActionPlanDrawer = () => {
    setTaskFormValues(null);
    setUIState(prev => ({ ...prev, isFormTaskDrawerOpen: false }));
  };

  const handleOnTaskUpdated = () => {
    refreshTaskData();
    onTaskUpdated && onTaskUpdated();
    handleCloseActionPlanDrawer();
  };

  const handleOpenChangeLogDrawer = () => {
    setUIState(prev => ({ ...prev, isChangeLogDrawerOpen: true }));
  };

  const handleCloseChangeLogDrawer = () => {
    setUIState(prev => ({ ...prev, isChangeLogDrawerOpen: false }));
  };

  const buttonDefinitions = buildCloseAndSaveButtonDefinition({
    onClose: () => onClose({ refreshData: false }),
    isSaveDisabled: status !== UIStatus.Success || uiState.isSaving || uiState.isLoading,
    isSaving: uiState.isSaving
  });

  return (
    <>
      <FPDrawer
        FPDrawerProps={{ open: true, onClose: () => onClose({ refreshData: false }) }}
        CardHeaderProps={{ title: COPIES.title }}
        onClose={onClose && onClose}
        ContentWrapper="form"
        ContentWrapperProps={{ onSubmit: handleSubmit(onSubmit) }}
        footer={
          <FPButtonBuilder
            GridContainerProps={{ spacing: 2 }}
            buttonDefinitions={buttonDefinitions}
          />
        }
      >
        <StatusManager
          status={status}
          slots={{
            Error: (
              <EmptyPlaceholder
                subtitle={error || strings.dailyPlan.drawer.emptyState}
                customEmptyState={<SvgNoResults height={400} />}
              />
            )
          }}
        >
          <>
            <DetailsContainer>
              <TaskDetails
                candidate={task?.candidate}
                date={formattedDate}
                entity={parsedEntity}
                entityType={task?.entityType}
                jobOrder={task?.jobOrder}
                sendout={task?.sendoutInterview?.sendout}
                status={task?.status}
                taskType={task?.taskType}
                customTask={task?.customTask}
                onEditTask={handleEditClickButton}
                onOpenChangeLogs={handleOpenChangeLogDrawer}
              />
            </DetailsContainer>
            <StyledDivider role="separator" />

            <FormContext {...form}>
              <TaskResults
                taskStatusId={task?.status?.id}
                taskTypeId={task?.taskType?.id}
                taskDate={task?.startDatetime}
              />
            </FormContext>

            <CommentsContainer>
              <CommentTimeline taskId={taskId} />
            </CommentsContainer>
          </>
        </StatusManager>
      </FPDrawer>

      <When condition={uiState.isFormTaskDrawerOpen}>
        <CreationTaskDrawer
          onClose={handleCloseActionPlanDrawer}
          defaultValues={taskFormValues}
          onTaskUpdated={handleOnTaskUpdated}
        />
      </When>

      <When condition={uiState.isChangeLogDrawerOpen}>
        <ChangeLogDrawer onClose={onClose} onBack={handleCloseChangeLogDrawer} taskId={taskId} />
      </When>
    </>
  );
};
